<template>
  <div class="activity-table">
    <recognition-filters
      :programs="programs"
      :program-id="programId"
      :selected-program="selectedProgram"
      :selected-date-range="selectedDateRange"
      :date-range-options="dateRangeOptions"
      :has-active-filters="hasActiveFilters"
      @update:selected-program="selectedProgram = $event"
      @update:selected-date-range="selectedDateRange = $event"
      @reset="resetFilters"
    >
      <template #prepend>
        <b-form-select
          v-if="hasScopeOptions"
          v-model="selectedScope"
          :options="scopeOptions"
          class="recognition-filters__filter"
        />
        <b-form-select
          v-if="hasSpendCategories"
          v-model="selectedSpendCategory"
          :options="spendCategoryOptions"
          class="recognition-filters__filter"
        />
      </template>
      <template #append>
        <export-button
          file-name="Recognition_Activity"
          :export-headers="exportHeaders"
          :export-data="activity"
          :export-file-rows="exportFileRow"
          :disabled="activity.length === 0"
          :enable-pdf="false"
          display-text="Export as"
        />
      </template>
    </recognition-filters>

    <app-loading :loading="isLoading" />

    <b-table
      v-if="activity.length > 0"
      :items="activity"
      :fields="tableFields"
      :tbody-tr-class="getRowClass"
      primary-key="id"
      hover
      responsive
      borderless
      show-empty
      class="activity-table__table"
    >
      <template #cell(created_at)="{ item }">
        <span>{{ formatDate(item.created_at) }}</span>
      </template>

      <template #cell(program_name)="{ item }">
        <div class="activity-table__award">
          <span>{{ item.program_name || '-' }}</span>
          <div v-if="item.is_hidden || item.withdrawn_at" class="activity-table__pills">
            <b-badge v-if="item.is_hidden" pill variant="danger">Hidden</b-badge>
            <b-badge v-if="item.withdrawn_at" pill variant="secondary">Withdrawn</b-badge>
          </div>
        </div>
      </template>

      <template #cell(program_type)="{ item }">
        <span>{{ formatType(item) }}</span>
      </template>

      <template #cell(recognised_by)="{ item }">
        <div class="activity-table__person">
          <span>{{ item.recognised_by || 'Automatic' }}</span>
          <div class="activity-table__person-meta">
            <small>{{ item.recognised_by_employee_code || '-' }}</small>
            <small>{{ formatDivisionIds(item.recognised_by_division_ids) }}</small>
          </div>
        </div>
      </template>

      <template #cell(nominee_name)="{ item }">
        <div class="activity-table__person">
          <span>{{ item.nominee_name || '-' }}</span>
          <div class="activity-table__person-meta">
            <small>{{ item.employee_code || '-' }}</small>
            <small>{{ formatDivisionIds(item.nominee_division_ids) }}</small>
          </div>
        </div>
      </template>

      <template #cell(company_values)="{ item }">
        <span>{{ formatCompanyValues(item.company_values) }}</span>
      </template>

      <template #cell(recipient_message)="{ item }">
        <span v-b-tooltip.hover :title="item.recipient_message" class="activity-table__message">
          {{ item.recipient_message || '-' }}
        </span>
      </template>

      <template #cell(spend_category)="{ item }">
        <span>{{ item.spend_category || '-' }}</span>
      </template>

      <template #cell(amount_minor)="{ item }">
        <span>{{ formatAmount(item.amount_minor) }}</span>
      </template>

      <template #cell(redeemed_at)="{ item }">
        <span>{{ formatDate(item.redeemed_at) }}</span>
      </template>

      <template #cell(redemption_grant_type)="{ item }">
        <span>{{ formatGrantType(item.redemption_grant_type) }}</span>
      </template>

      <template #cell(expires_at)="{ item }">
        <span>{{ formatDate(item.expires_at) }}</span>
      </template>

      <template #cell(actions)="{ item }">
        <b-dropdown
          v-if="item.is_withdrawable"
          right
          no-caret
          variant="link"
          menu-class="activity-table__actions-menu"
          toggle-class="activity-table__actions-toggle"
          class="activity-table__actions"
        >
          <template #button-content>
            <font-awesome-icon :icon="['far', 'ellipsis-vertical']" />
            <span class="sr-only">Actions</span>
          </template>
          <b-dropdown-item @click="openWithdrawModal(item)"> Withdraw monetary award </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <div v-else-if="!isLoading" class="activity-table__empty">
      <p class="text-muted">No recognition activity found.</p>
    </div>

    <withdraw-grant-modal ref="withdrawModal" :award="selectedAward" @withdrawn="emit('withdrawn')" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { format, parseISO } from 'date-fns';
import { useQuery, keepPreviousData } from '@tanstack/vue-query';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import WithdrawGrantModal from '@/pages/recognition/components/WithdrawGrantModal.vue';
import { toCurrency } from '@/utils';
import { getDateFromForRange, RECOGNITION_REDEMPTION_METHOD_CHOICES } from '@/pages/recognition/constants';
import { spendCategoryFilterOptions } from '@/pages/recognition/utils/spendCategories';
import { getRecognitionActivity } from '@/queries/recognition';
import useDivisionNames from '@/pages/recognition/composables/useDivisionNames';
import useScopeFilter from '@/pages/recognition/composables/useScopeFilter';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
  showHidden: {
    type: Boolean,
    default: false,
  },
  // Recognition viewers: filters and export stay, the Withdraw action goes.
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['withdrawn']);

const { selected: selectedScope, options: scopeOptions, scopeParam, hasOptions: hasScopeOptions } = useScopeFilter();
const { formatDivisionIds } = useDivisionNames();

const selectedProgram = ref(null);
const selectedSpendCategory = ref(null);
const selectedDateRange = ref('last_30_days');

const PERSON_COLUMN_WIDTH = '9rem';

const ACTIVITY_TABLE_FIELDS = [
  { key: 'created_at', label: 'Date', tdClass: 'text-nowrap' },
  { key: 'program_name', label: 'Award' },
  { key: 'program_type', label: 'Type' },
  { key: 'recognised_by', label: 'From', thStyle: { width: PERSON_COLUMN_WIDTH, minWidth: PERSON_COLUMN_WIDTH } },
  { key: 'nominee_name', label: 'To', thStyle: { width: PERSON_COLUMN_WIDTH, minWidth: PERSON_COLUMN_WIDTH } },
  { key: 'company_values', label: 'Values' },
  { key: 'recipient_message', label: 'Message' },
  { key: 'spend_category', label: 'Category' },
  { key: 'amount_minor', label: 'Amount' },
  { key: 'redeemed_at', label: 'Redeemed' },
  { key: 'redemption_grant_type', label: 'Method' },
  { key: 'expires_at', label: 'Expiry' },
  { key: 'actions', label: '' },
];

const tableFields = computed(() =>
  props.readonly ? ACTIVITY_TABLE_FIELDS.filter(field => field.key !== 'actions') : ACTIVITY_TABLE_FIELDS,
);

// A row's type is the combination of its kind (award vs program nomination
// message) and the program type. recognition rows only ever exist for PROGRAM
// programs; award rows exist for all three program types.
const TYPE_LABELS = {
  'award:OPEN': 'Open recognition',
  'award:PROGRAM': 'Program award',
  'award:MILESTONE': 'Milestone award',
  'recognition:PROGRAM': 'Program nomination message',
};

const dateRangeOptions = [
  { value: 'last_30_days', text: 'Last 30 days' },
  { value: 'last_90_days', text: 'Last 90 days' },
  { value: 'last_year', text: 'Last year' },
  { value: null, text: 'All time' },
];

// Single source of truth for redemption-method labels, shared with the other
// recognition tabs so the same grant type reads identically everywhere.
const GRANT_TYPE_LABELS = Object.fromEntries(
  RECOGNITION_REDEMPTION_METHOD_CHOICES.map(({ value, text }) => [value, text]),
);

const currency = computed(() => State.state?.company?.default_currency);

const spendCategoryOptions = computed(() => spendCategoryFilterOptions(props.programs));

const hasSpendCategories = computed(() => spendCategoryOptions.value.length > 1);

const hasActiveFilters = computed(() => {
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  return (
    selectedScope.value !== null ||
    selectedSpendCategory.value !== null ||
    hasProgramFilter ||
    selectedDateRange.value !== 'last_30_days'
  );
});

const activityParams = computed(() => {
  const params = {};
  if (props.programId) {
    params.recognition_award_program_id = props.programId;
  } else if (selectedProgram.value) {
    params.recognition_award_program_id = selectedProgram.value;
  }
  if (scopeParam.value) {
    params.scope = scopeParam.value;
  }
  if (selectedSpendCategory.value) {
    params.spend_category = selectedSpendCategory.value;
  }
  const dateFrom = getDateFromForRange(selectedDateRange.value);
  if (dateFrom) {
    params.date_from = dateFrom;
  }
  if (props.showHidden) {
    params.include_hidden = true;
  }
  return params;
});

// keepPreviousData keeps the table visible (and avoids the full-page logo) while
// a filter change refetches, matching the analytics tab's loading behaviour.
const { data: activityData, isLoading } = useQuery({
  ...getRecognitionActivity(activityParams),
  placeholderData: keepPreviousData,
});

const activity = computed(() => {
  const rows = activityData.value || [];
  if (props.showHidden) {
    return rows;
  }
  return rows.filter(row => !row.withdrawn_at);
});

const withdrawModal = ref(null);
const selectedAward = ref(null);

const openWithdrawModal = item => {
  selectedAward.value = item;
  withdrawModal.value?.show();
};

const getRowClass = item => (item.is_hidden || item.withdrawn_at ? 'activity-table__row--muted' : '');

const resetFilters = () => {
  selectedScope.value = null;
  selectedSpendCategory.value = null;
  if (!props.programId) {
    selectedProgram.value = null;
  }
  selectedDateRange.value = 'last_30_days';
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return format(parseISO(dateStr), 'd MMM yyyy');
};

const formatType = item => TYPE_LABELS[`${item.kind}:${item.program_type}`] || item.program_type || '-';

const formatCompanyValues = values => (values && values.length ? values.join(', ') : '-');

const isEmptyAmount = value => value === null || value === undefined || value === 0;

const formatAmount = value => (isEmptyAmount(value) ? '-' : toCurrency(value, currency.value));

const formatGrantType = grantType => GRANT_TYPE_LABELS[grantType] || '-';

const toExportNumber = value => (isEmptyAmount(value) ? '' : (value / 100).toFixed(2));

const exportHeaders = [
  'Date',
  'Award',
  'Type',
  'From',
  'From employee no',
  'From division',
  'To',
  'To employee no',
  'To division',
  'Values',
  'Message',
  'Category',
  'Amount',
  'Redeemed',
  'Method',
  'Expiry',
  'Withdrawn',
  'Hidden',
];

const exportFileRow = (item, format) => {
  const isDataExport = format === 'csv' || format === 'xlsx';
  return [
    formatDate(item.created_at),
    item.program_name || '-',
    formatType(item),
    item.recognised_by || 'Automatic',
    item.recognised_by_employee_code || '-',
    formatDivisionIds(item.recognised_by_division_ids),
    item.nominee_name || '-',
    item.employee_code || '-',
    formatDivisionIds(item.nominee_division_ids),
    formatCompanyValues(item.company_values),
    item.recipient_message || '-',
    item.spend_category || '-',
    isDataExport ? toExportNumber(item.amount_minor) : formatAmount(item.amount_minor),
    formatDate(item.redeemed_at),
    formatGrantType(item.redemption_grant_type),
    formatDate(item.expires_at),
    formatDate(item.withdrawn_at),
    item.is_hidden ? 'Yes' : '-',
  ];
};
</script>

<style scoped lang="scss">
.activity-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 0.6rem;
    }

    :deep(tbody td) {
      padding: 1.25rem 0.6rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }

    :deep(.activity-table__row--muted) {
      color: var(--palette-color-default-lighten-40);
    }
  }

  &__award {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  &__person {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
  }

  &__person-meta {
    display: flex;
    flex-direction: column;
    margin-top: 0.25rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__message {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 12rem;
    white-space: normal;
    vertical-align: middle;
  }

  &__actions :deep(.activity-table__actions-toggle) {
    color: var(--palette-color-default-lighten-40);
    // Match the table body text: 13px, normal weight, vertically centred.
    font-size: 0.8125rem;
    line-height: 1;
    vertical-align: middle;
    padding: 0.25rem 0.5rem;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    outline: none !important;

    &:hover,
    &:focus,
    &:active {
      color: var(--palette-color-brand-primary);
    }
  }

  &__actions :deep(.activity-table__actions-menu) {
    padding: 0;

    .dropdown-item {
      padding: 0.5rem 1rem;
    }
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
